import type { AuthUser, UserRole } from "@mvk/shared";
import type { User } from "../types";
import { isApiEnabled } from "../api/config";
import { visitorAuthApi } from "../api/auth";
import { visitorAuthStorage } from "../api/authStorage";
import { apiRequest } from "../api/client";
import { hydrateCameraGrantFromServer } from "./cameraPermission";

export type EventRolePayload = {
  eventRole: UserRole;
  companyId?: string;
  companyName?: string;
  booth?: string;
  boothQrCode?: string;
  companyQrCode?: string;
  representativeId?: string;
  badgeQrCode?: string;
  fullName?: string;
  position?: string;
};

export function mapAuthUserToProfile(user: AuthUser): Partial<User> {
  hydrateCameraGrantFromServer(user.id, user.cameraGranted);
  return {
    id: user.id,
    fullName: user.fullName,
    phone: user.phone,
    email: user.email,
    position: user.position,
    companyName: user.companyName,
    sharePhoneOnScan: user.sharePhoneOnScan !== false,
    shareEmailOnScan: user.shareEmailOnScan !== false,
  };
}

export const visitorSessionService = {
  async syncProfile(updateUser: (partial: Partial<User>) => void): Promise<void> {
    if (!isApiEnabled() || !visitorAuthStorage.isAuthenticated()) return;

    const payload = await visitorAuthApi.me();
    updateUser(mapAuthUserToProfile(payload.user));
  },

  async updateExhibitorProfile(
    eventId: string,
    patch: {
      fullName?: string;
      position?: string;
      email?: string;
      phone?: string;
      /** Visitor-facing phone; empty string clears override. Does not change login phone. */
      publicPhone?: string;
      tgUsername?: string;
    },
  ): Promise<{
    user: Partial<User>;
    representative?: import("../types").Representative;
  }> {
    const data = await apiRequest<{
      user: {
        id: string;
        fullName: string;
        email?: string;
        phone?: string;
        position?: string;
        companyName?: string;
      };
      representative?: import("@mvk/shared").RepresentativeSummary;
    }>("/me/profile", {
      method: "PATCH",
      body: { eventId, ...patch },
      audience: "visitor",
    });
    return {
      user: mapAuthUserToProfile({
        ...data.user,
        isPlatformAdmin: false,
      } as import("@mvk/shared").AuthUser),
      representative: data.representative
        ? {
            id: data.representative.id,
            companyId: data.representative.companyId,
            eventId: data.representative.eventId,
            fullName: data.representative.fullName,
            position: data.representative.position,
            photo: data.representative.photoUrl,
            bio: data.representative.bio,
            phone: data.representative.phone,
            publicPhone: data.representative.publicPhone,
            email: data.representative.email,
            telegram: data.representative.telegram,
            badgeQrCode: data.representative.badgeQrCode ?? "",
            badgeBarcode: data.representative.badgeBarcode,
          }
        : undefined,
    };
  },

  async fetchEventRole(eventId: string): Promise<EventRolePayload | null> {
    if (!isApiEnabled() || !visitorAuthStorage.isAuthenticated() || !eventId) return null;
    try {
      return await apiRequest<EventRolePayload>(`/me/events/${eventId}/role`, {
        audience: "visitor",
      });
    } catch {
      return null;
    }
  },

  async syncEventRole(
    eventId: string,
    apply: (role: EventRolePayload | null) => void,
  ): Promise<void> {
    const role = await this.fetchEventRole(eventId);
    apply(role);
  },

  /** Keep last_seen_at fresh while the visitor app tab is open. */
  startPresenceHeartbeat(): () => void {
    if (!isApiEnabled() || !visitorAuthStorage.isAuthenticated()) {
      return () => undefined;
    }
    let stopped = false;
    const tick = () => {
      if (stopped || document.visibilityState === "hidden") return;
      void visitorAuthApi.presence().catch(() => undefined);
    };
    tick();
    const id = window.setInterval(tick, 2 * 60 * 1000);
    const onVis = () => {
      if (document.visibilityState === "visible") tick();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      stopped = true;
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
    };
  },
};
